import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {

  DndContext,

  PointerSensor,

  closestCenter,

  useSensor,

  useSensors,

} from "@dnd-kit/core";

import {

  SortableContext,

  arrayMove,

  useSortable,

  verticalListSortingStrategy,

} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { createStaffUser } from "../api/salonUsers";
import { reorderStaff } from "../api/staffs";
import { sendStaffInvites } from "../api/staffInvites";
import SBINModal from "../components/modals/SBINModal";
import StaffCredentialModal from "../components/staff/StaffCredentialModal";
import StaffImportWizard from "../components/staff/StaffImportWizard";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";
import { API_BASE as API } from "../api/base";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../utils/phone";
import { publishStaffScheduleUpdate } from "../utils/staffScheduleSync";
import "./HrSectionCompact.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
} from "../ui/modalStyles";



const MANAGER_ROLES = ["admin", "host", "owner", "manager", "solo_technician"];

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "technician", label: "Technician" },
  { value: "apprentice", label: "Apprentice" },
];

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Create staff profiles, manage visibility, and keep schedule order updated.",
    anchorId: "ms-overview",
    bullets: [
      "Use Import Staff for bulk adds.",
      "Use Add Staff form for one staff member at a time.",
      "Use Staff list actions to edit status and settings.",
    ],
    tips: [
      "Keep display names consistent with schedule and payroll records.",
      "Review active status weekly to avoid booking mistakes.",
    ],
  },
  {
    id: "imports",
    title: "Import tools",
    description: "Use import tools to add many staff records quickly and review previous imports.",
    anchorId: "ms-import",
    bullets: [
      "Import Staff opens bulk upload wizard.",
      "Import History shows past import jobs and results.",
      "Invite Staff By Email sends activation links to existing or brand-new staff emails.",
    ],
    tips: ["Use import for large updates and Add Staff for single corrections."],
  },
  {
    id: "add-staff",
    title: "Add staff form",
    description: "Fill profile, role, and login details to create a staff account.",
    anchorId: "ms-add",
    bullets: [
      "Display name and email are required.",
      "Optional username and password can be set now or generated.",
      "Schedule order controls display order when allowed by role.",
    ],
    tips: ["Use valid phone format to reduce contact errors."],
  },
  {
    id: "staff-list",
    title: "Staff list and order",
    description: "Review all staff and drag rows to change schedule order when permitted.",
    anchorId: "ms-list",
    bullets: [
      "Drag handle reorders staff for schedule display.",
      "Status columns show active and service settings.",
    ],
    tips: ["Save order changes and confirm updated order before leaving."],
  },
  {
    id: "staff-actions",
    title: "Staff actions",
    description: "Use row actions to maintain staff access and booking visibility.",
    anchorId: "ms-actions",
    bullets: [
      "Activate/Deactivate controls account status.",
      "Uses Time Clock affects payroll/time clock pages.",
      "Show in Online Booking controls booking visibility.",
    ],
    tips: ["Change one setting at a time and wait for save confirmation."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Cannot create staff",
    body: "Check required fields, email format, and phone digits.",
  },
  {
    title: "Cannot reorder staff",
    body: "Only roles with schedule order permissions can drag rows.",
  },
  {
    title: "Setting does not update",
    body: "Retry after save completes, then refresh the staff list.",
  },
];

const TOOLTIPS = {
  display_name: "Name shown on schedule, reports, and booking screens.",
  role: "Controls staff permissions and default access.",
  phone: "Use a 10-digit North American phone number.",
  email: "Used for login and staff communication.",
  username: "Optional login name. Email can also be used.",
  temp_password: "Leave blank to auto-generate a temporary password.",
  schedule_order: "Lower values appear earlier in schedule order.",
  drag: "Drag to reorder staff in schedule order.",
  perform_services: "Allow this staff member to be assigned and booked for services. Staff who are active and perform services are included in billing-period staff counts.",
  uses_time_clock: "Include this staff member in time clock and payroll.",
  online_booking: "Show or hide this staff member in online booking.",
  invite_salon: "Choose which salon location this invitation belongs to.",
  invite_expiration: "Invitation links can expire after a selected number of hours.",
  invite_staff: "Select existing staff with email addresses to send invitations quickly.",
  invite_recipients:
    "Enter brand-new recipient emails separated by commas, semicolons, spaces, or new lines.",
};

const INVITE_EXPIRATION_OPTIONS = [
  { value: "1h", label: "1 hour" },
  { value: "12h", label: "12 hours" },
  { value: "24h", label: "24 hours" },
  { value: "36h", label: "36 hours" },
  { value: "never", label: "Does not expire" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;


async function readErrorDetail(res) {

  try {

    const data = await res.json();

    if (typeof data?.detail === "string" && data.detail.trim()) return data.detail.trim();

    if (typeof data?.message === "string" && data.message.trim()) return data.message.trim();

    return null;

  } catch (_) {

    try {

      const text = await res.text();

      return text?.trim() || null;

    } catch (_) {

      return null;

    }

  }

}



function toFriendlyStaffError(detail, status) {

  const d = (detail || "").toLowerCase();

  if (d.includes("primary owner") && d.includes("cannot") && d.includes("deactiv")) {

    return "You can’t deactivate the primary owner account. Transfer ownership first if needed.";

  }

  if (d.includes("owner") && d.includes("cannot") && d.includes("deactiv")) {

    return "You can’t deactivate the primary owner account. Transfer ownership first if needed.";

  }

  if (status === 403) {

    return detail || "You don’t have permission to do that.";

  }

  return detail || "Unable to update staff. Please try again.";

}



function SortableStaffRow({
  staffMember,
  canDrag,
  onToggleActive,
  onTogglePerformServices,
  onToggleUsesTimeClock,
  onToggleShowOnlineBooking,
  orderValue,
  isSaving,
  canManageTimeClock,
  canManageOnlineBooking,
}) {
  const {

    attributes,

    listeners,

    setNodeRef,

    setActivatorNodeRef,

    transform,

    transition,

    isDragging,

  } = useSortable({

    id: staffMember.id,

    disabled: !canDrag,

  });



  const style = {

    transform: CSS.Transform.toString(transform),

    transition,

  };

  const dragClass = isDragging ? "bg-slate-50" : "";



  const actionsDisabled = Boolean(isSaving);
  const activeToggleDisabled = actionsDisabled;
  const performServicesToggleDisabled = actionsDisabled;
  const usesTimeClockDisabled = actionsDisabled || !canManageTimeClock;
  const showOnlineDisabled = actionsDisabled || !canManageOnlineBooking;

  return (
    <tr ref={setNodeRef} style={style} className={`hover:bg-slate-50 ${dragClass}`}>
      <td className="px-4 py-3 whitespace-nowrap">
        <button
          type="button"
          ref={setActivatorNodeRef}

          {...attributes}

          {...listeners}

          disabled={!canDrag}

          className={`text-slate-400 hover:text-slate-600 text-lg leading-none ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-default"

            }`}

          aria-label="Drag to reorder"

        >
          ||
        </button>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {orderValue ?? "-"}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <Link
          to={`/staff/${staffMember.id}`}
          className="text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          Edit
        </Link>
      </td>
      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
        <Link to={`/staff/${staffMember.id}`} className="text-slate-900 hover:underline">
          {staffMember.display_name || staffMember.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">

        {staffMember.role || ""}

      </td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
        {staffMember.phone ? formatNANP(staffMember.phone) : ""}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <label
          className={`inline-flex items-center gap-2 text-xs font-semibold ${
            activeToggleDisabled
              ? "cursor-not-allowed text-slate-400"
              : "cursor-pointer text-slate-700"
          }`}
        >
          <input
            type="checkbox"
            checked={!!staffMember.is_active}
            onChange={() => onToggleActive(staffMember.id, staffMember.is_active)}
            disabled={activeToggleDisabled}
            className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
            aria-label={`Set ${staffMember.display_name || staffMember.name} active status`}
          />
          <span>{staffMember.is_active ? "Active" : "Inactive"}</span>
        </label>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-wrap items-center gap-3">
          <label
            className={`flex items-center gap-2 text-xs font-semibold ${
              performServicesToggleDisabled
                ? "cursor-not-allowed text-slate-400"
                : "cursor-pointer text-slate-600"
            }`}
          >
            <input
              type="checkbox"
              checked={!!staffMember.perform_services}
              onChange={() =>
                onTogglePerformServices(staffMember.id, staffMember.perform_services)
              }
              disabled={performServicesToggleDisabled}
              className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
              aria-label={`Set ${staffMember.display_name || staffMember.name} perform services`}
            />
            <span>Perform Services {staffMember.perform_services ? "On" : "Off"}</span>
          </label>
          <label
            className={`flex items-center gap-2 text-xs font-semibold text-slate-600 ${
              usesTimeClockDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            <input
              type="checkbox"
              checked={!!staffMember.uses_time_clock}
              onChange={() =>
                onToggleUsesTimeClock(staffMember.id, staffMember.uses_time_clock)
              }
              disabled={usesTimeClockDisabled}
              className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
            />
            <span>Uses Time Clock</span>
          </label>
          <label
            className={`flex items-center gap-2 text-xs font-semibold text-slate-600 ${
              showOnlineDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            <input
              type="checkbox"
              checked={!!staffMember.show_in_online_booking}
              onChange={() =>
                onToggleShowOnlineBooking(
                  staffMember.id,
                  staffMember.show_in_online_booking
                )
              }
              disabled={showOnlineDisabled}
              className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
            />
            <span>Show in Online Booking</span>
          </label>
          {actionsDisabled && (
            <span className="text-xs text-slate-400">Saving...</span>
          )}
        </div>
      </td>
    </tr>
  );
}


export default function ManageStaff() {
  const navigate = useNavigate();
  const { token, role, user, accessibleSalons } = useAuth();
  const normalizedRole = (role || "").toLowerCase();

  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [reorderNotice, setReorderNotice] = useState("");
  const [reorderSaving, setReorderSaving] = useState(false);
  const [savingById, setSavingById] = useState({});
  const [hideInactiveStaff, setHideInactiveStaff] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);


  const [form, setForm] = useState({
    display_name: "",

    role: "technician",

    email: "",

    phone: "",

    username: "",

    temp_password: "",

    display_order: "",

  });
  const [credentialModal, setCredentialModal] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteSelectedSalonId, setInviteSelectedSalonId] = useState(null);
  const [inviteExpiration, setInviteExpiration] = useState("24h");
  const [inviteStaffIds, setInviteStaffIds] = useState([]);
  const [inviteRecipientText, setInviteRecipientText] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteFormError, setInviteFormError] = useState("");
  const [inviteFailures, setInviteFailures] = useState([]);


  const authHeaders = useMemo(

    () =>

      token

        ? {

          Authorization: `Bearer ${token}`,

          "Content-Type": "application/json",

        }

        : { "Content-Type": "application/json" },

    [token]

  );
  const canAssignOwner = normalizedRole === "owner" || normalizedRole === "admin";
  const canEditScheduleOrder =
    normalizedRole === "admin" || normalizedRole === "owner" || normalizedRole === "manager";
  const canManageTimeClock =
    normalizedRole === "admin" || normalizedRole === "owner" || normalizedRole === "manager";
  const canManageOnlineBooking =
    normalizedRole === "owner" ||
    normalizedRole === "manager" ||
    normalizedRole === "admin" ||
    normalizedRole === "host";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const publishScheduleStaffUpdate = useCallback(
    (detail = {}) => {
      if (!salonId) return;
      publishStaffScheduleUpdate(salonId, detail);
    },
    [salonId]
  );
  const { permissions: effectivePermissions, loading: permissionsLoading } =
    useMyPermissions({ enabled: Boolean(token && salonId) });
  const canAccessStaffManagement =
    ["admin", "host"].includes(normalizedRole) ||
    Boolean(user?.is_super_admin) ||
    (effectivePermissions === null
      ? MANAGER_ROLES.includes(normalizedRole)
      : Boolean(effectivePermissions?.["staff.manage"]));

  // Redirect guards
  useEffect(() => {
    if (!token) {
      navigate("/login?next=/manager/manage/staff", { replace: true });
      return;
    }
    if (!salonId) {
      setError("Select a salon to manage staff.");
      const timeout = setTimeout(() => {
        navigate("/my-salons", { replace: true });
      }, 600);
      return () => clearTimeout(timeout);
    }
    if (permissionsLoading || effectivePermissions === null) {
      return;
    }
    if (!canAccessStaffManagement) {
      navigate("/my-bookings", { replace: true });
    }
  }, [
    token,
    salonId,
    navigate,
    permissionsLoading,
    effectivePermissions,
    canAccessStaffManagement,
  ]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Load staff for the current salon context
  useEffect(() => {
    if (!token || !salonId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        await refreshStaff();
      } catch (err) {
        setError(err.message || "Failed to load staff");
      } finally {
        setLoading(false);
      }
    };



    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, salonId, authHeaders]);

  const refreshStaff = async () => {
    if (!salonId) return;
    try {
      const res = await fetch(`${API}/staffs`, {
        headers: authHeaders,
      });
      if (res.ok) {
        setStaff(await res.json());
      }
    } catch (err) {
      setError(err.message || "Failed to refresh staff");
    }
  };

  const showToast = (message) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
    }, 3000);
  };

  const inviteSalonOptions = useMemo(() => {
    const list = Array.isArray(accessibleSalons) ? accessibleSalons : [];
    return list
      .map((entry) => {
        const idValue = entry?.id ?? entry?.salon_id ?? entry?.salonId;
        const nameValue = entry?.name ?? entry?.salon_name ?? "Salon";
        const idNumber = Number(idValue);
        if (!Number.isFinite(idNumber)) return null;
        return {
          id: idNumber,
          name: nameValue,
        };
      })
      .filter(Boolean);
  }, [accessibleSalons]);

  const hasMultipleInviteSalons = inviteSalonOptions.length > 1;

  useEffect(() => {
    if (!inviteModalOpen) return;
    const preferredSalonId = Number(salonId);
    if (Number.isFinite(preferredSalonId) && preferredSalonId > 0) {
      setInviteSelectedSalonId(preferredSalonId);
      return;
    }
    if (inviteSalonOptions.length) {
      setInviteSelectedSalonId(inviteSalonOptions[0].id);
    }
  }, [inviteModalOpen, salonId, inviteSalonOptions]);

  const staffWithEmail = useMemo(
    () =>
      (Array.isArray(staff) ? staff : []).filter((entry) => {
        const emailValue = String(entry?.email || "").trim();
        return Boolean(emailValue);
      }),
    [staff]
  );

  const parseInviteEmails = useCallback((rawValue) => {
    if (!rawValue) return { valid: [], invalid: [] };
    const tokens = String(rawValue)
      .split(/[\s,;]+/)
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);
    const seen = new Set();
    const valid = [];
    const invalid = [];
    tokens.forEach((emailValue) => {
      if (seen.has(emailValue)) return;
      seen.add(emailValue);
      if (EMAIL_RE.test(emailValue)) {
        valid.push(emailValue);
      } else {
        invalid.push(emailValue);
      }
    });
    return { valid, invalid };
  }, []);

  const resetInviteForm = useCallback(() => {
    setInviteStaffIds([]);
    setInviteRecipientText("");
    setInviteExpiration("24h");
    setInviteFormError("");
    setInviteFailures([]);
  }, []);

  const openInviteModal = () => {
    resetInviteForm();
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteSending(false);
  };

  const toggleInviteStaffId = (staffId) => {
    setInviteStaffIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((entry) => entry !== staffId)
        : [...prev, staffId]
    );
  };

  const toInviteFailureMessage = useCallback((item) => {
    const reasonCode = String(item?.reason || "").toUpperCase();
    if (reasonCode === "EMAIL_INVALID") return "Invalid email format.";
    if (reasonCode === "STAFF_NOT_FOUND") return "Staff record not found.";
    if (reasonCode === "STAFF_EMAIL_REQUIRED") return "Staff member is missing an email.";
    if (reasonCode === "EMAIL_SEND_FAILED") return "Email delivery failed.";
    if (reasonCode === "INVITE_CONFLICT") return "Invitation already exists.";
    if (reasonCode === "INVITE_CREATE_FAILED") return "Could not create invitation.";
    return item?.reason || "Failed to send invitation.";
  }, []);

  const handleSendInvites = async () => {
    setInviteFormError("");
    setInviteFailures([]);

    const targetSalonId = Number(inviteSelectedSalonId || salonId);
    if (!Number.isFinite(targetSalonId) || targetSalonId <= 0) {
      setInviteFormError("Select a salon location.");
      return;
    }

    const selectedIds = Array.from(
      new Set(
        inviteStaffIds
          .map((entry) => Number(entry))
          .filter((entry) => Number.isFinite(entry) && entry > 0)
      )
    );
    const parsedEmails = parseInviteEmails(inviteRecipientText);
    if (parsedEmails.invalid.length) {
      setInviteFormError(
        `Invalid emails: ${parsedEmails.invalid.slice(0, 5).join(", ")}`
      );
      return;
    }

    if (!selectedIds.length && !parsedEmails.valid.length) {
      setInviteFormError("Select existing staff or enter at least one recipient email.");
      return;
    }

    setInviteSending(true);
    try {
      const result = await sendStaffInvites(
        {
          salonId: targetSalonId,
          emails: parsedEmails.valid,
          expiration: inviteExpiration,
          staffIds: selectedIds,
        },
        token
      );

      const sentCount = Number(result?.sent || 0);
      const failures = Array.isArray(result?.failed) ? result.failed : [];

      if (sentCount > 0) {
        showToast("Email sent.");
      }

      if (failures.length) {
        setInviteFailures(failures);
        if (!sentCount) {
          setInviteFormError("No invitation emails were sent.");
        }
      } else {
        closeInviteModal();
      }

      await refreshStaff();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        err?.message ||
        "Unable to send invitations.";
      setInviteFormError(message);
    } finally {
      setInviteSending(false);
    }
  };


  const normalizeOrderValue = (value) => {

    if (value == null) return null;

    const trimmed = String(value).trim();

    if (!trimmed) return null;

    const parsed = Number(trimmed);

    if (!Number.isFinite(parsed)) return null;

    return Math.max(0, Math.floor(parsed));

  };



  const handleDragEnd = async (event) => {
    if (!canEditScheduleOrder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceIds = (hideInactiveStaff ? visibleStaff : staff).map((entry) => entry.id);
    const oldIndex = sourceIds.findIndex((id) => id === active.id);
    const newIndex = sourceIds.findIndex((id) => id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const previous = staff;
    const reorderedSourceIds = arrayMove(sourceIds, oldIndex, newIndex);
    const allIds = staff.map((entry) => entry.id);
    const sourceIdSet = new Set(sourceIds);
    let sourceCursor = 0;
    const nextOrderIds = hideInactiveStaff
      ? allIds.map((id) =>
          sourceIdSet.has(id) ? reorderedSourceIds[sourceCursor++] : id
        )
      : reorderedSourceIds;
    const staffById = new Map(staff.map((entry) => [entry.id, entry]));
    const next = nextOrderIds
      .map((id, idx) => {
        const entry = staffById.get(id);
        if (!entry) return null;
        return { ...entry, display_order: idx + 1 };
      })
      .filter(Boolean);
    setStaff(next);
    if (!salonId) return;


    setReorderSaving(true);

    setReorderNotice("Saving...");

    try {

      const updated = await reorderStaff(

        {

          salon_id: salonId,

          ordered_staff_ids: next.map((entry) => entry.id),
        },
        token
      );

      if (Array.isArray(updated)) {

        setStaff(updated);

      }

      setReorderNotice("Order saved");

      setTimeout(() => setReorderNotice(""), 1500);

    } catch (err) {

      setStaff(previous);

      setReorderNotice("");

      setError(err?.message || "Unable to save schedule order.");

    } finally {
      setReorderSaving(false);
    }
  };

  const staffCounts = useMemo(() => {
    const total = Array.isArray(staff) ? staff.length : 0;
    const active = (staff || []).filter((entry) => Boolean(entry?.is_active)).length;
    const inactive = Math.max(0, total - active);
    return { total, active, inactive };
  }, [staff]);

  const visibleStaff = useMemo(() => {
    if (!hideInactiveStaff) return staff;
    return (staff || []).filter((entry) => Boolean(entry?.is_active));
  }, [hideInactiveStaff, staff]);

  const fallbackOrderById = useMemo(() => {
    const map = new Map();
    (staff || []).forEach((entry, idx) => {
      map.set(entry.id, idx + 1);
    });
    return map;
  }, [staff]);


  const handleCreate = async (e) => {
    e.preventDefault();
    const displayName = form.display_name.trim();
    const emailValue = form.email.trim();
    const phoneDigits = form.phone ? toNANPDigits10(form.phone) : "";
    if (!displayName || !emailValue || !salonId) {
      setError("Display name and email are required");
      return;
    }
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setPhoneError("Phone must be a 10-digit North American number.");
      return;
    }
    setSaving(true);
    setError("");
    setPhoneError("");
    try {
      const tempPassword = form.temp_password.trim();
      const payload = {
        salon_id: salonId,
        display_name: displayName,
        role: form.role,
        email: emailValue,
        phone: phoneDigits || null,
        username: form.username?.trim() || undefined,
        temp_password: tempPassword || undefined,
      };
      const displayOrderValue = normalizeOrderValue(form.display_order);

      if (canEditScheduleOrder && displayOrderValue !== null) {

        payload.display_order = displayOrderValue;

      } else if (canEditScheduleOrder && form.display_order === "") {

        payload.display_order = null;

      }

      const response = await createStaffUser(payload, token);

      setCredentialModal({

        display_name: displayName,

        salon_id: response.salon_id,

        username: response.username,

        temp_password: response.temp_password,

        must_change_password: response.must_change_password,

      });

      setForm({

        display_name: "",

        role: "technician",

        email: "",

        phone: "",

        username: "",

        temp_password: "",

        display_order: "",

      });

      await refreshStaff(salonId);

    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === "string" ? detail : detail?.message) ||
        err?.message ||
        "Failed to create staff member";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const setRowSaving = (staffId, value) => {
    setSavingById((prev) => ({
      ...prev,
      [staffId]: value,
    }));
  };

  const toggleActive = async (staffId, current) => {
    const staffMember = (staff || []).find((entry) => entry.id === staffId);
    const nextActive = !current;
    if (staffMember?.perform_services) {
      const confirmed = window.confirm(
        nextActive
          ? "Turning this staff member active may include them in the billing-period staff count if they perform services. Continue?"
          : "Turning this staff member inactive may not reduce the current billing-period staff count if they already performed services or were active in this billing period. Continue?"
      );
      if (!confirmed) return;
    }
    setRowSaving(staffId, true);
    try {
      const res = await fetch(`${API}/staffs/${staffId}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ is_active: nextActive }),
      });

      if (!res.ok) {

        const detail = await readErrorDetail(res);

        throw new Error(toFriendlyStaffError(detail, res.status));

      }

      await refreshStaff(salonId);
    } catch (err) {
      setError(err?.message || "Unable to update staff. Please try again.");
    } finally {
      setRowSaving(staffId, false);
    }
  };

  const togglePerformServices = async (staffId, current) => {
    const nextPerformServices = !current;
    const staffMember = (staff || []).find((entry) => entry.id === staffId);
    if (staffMember?.is_active) {
      const confirmed = window.confirm(
        nextPerformServices
          ? "Turning Perform Services on may include this staff member in the billing-period staff count. Continue?"
          : "Turning Perform Services off may not reduce the current billing-period staff count if this staff member already counted in this billing period. Continue?"
      );
      if (!confirmed) return;
    }
    const payload = nextPerformServices
      ? { perform_services: true }
      : { perform_services: false, show_in_online_booking: false };
    setRowSaving(staffId, true);
    try {
      const res = await fetch(`${API}/staffs/${staffId}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(toFriendlyStaffError(detail, res.status));
      }
      const scheduleUpdateDetail = {
        staffId,
        perform_services: nextPerformServices,
      };
      if (!nextPerformServices) {
        scheduleUpdateDetail.show_in_online_booking = false;
      }
      publishScheduleStaffUpdate(scheduleUpdateDetail);
      await refreshStaff(salonId);
    } catch (err) {
      setError(err?.message || "Unable to update staff. Please try again.");
    } finally {
      setRowSaving(staffId, false);
    }
  };

  const toggleUsesTimeClock = async (staffId, current) => {
    const nextValue = !current;
    setRowSaving(staffId, true);
    setStaff((prev) =>
      prev.map((entry) =>
        entry.id === staffId ? { ...entry, uses_time_clock: nextValue } : entry
      )
    );
    try {
      const res = await fetch(`${API}/staffs/${staffId}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ uses_time_clock: nextValue }),
      });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(toFriendlyStaffError(detail, res.status));
      }
      const updated = await res.json();
      setStaff((prev) =>
        prev.map((entry) =>
          entry.id === staffId
            ? { ...entry, uses_time_clock: !!updated.uses_time_clock }
            : entry
        )
      );
    } catch (err) {
      setStaff((prev) =>
        prev.map((entry) =>
          entry.id === staffId ? { ...entry, uses_time_clock: current } : entry
        )
      );
      showToast("Unable to update Uses Time Clock.");
    } finally {
      setRowSaving(staffId, false);
    }
  };

  const toggleShowOnlineBooking = async (staffId, current) => {
    const nextValue = !current;
    setRowSaving(staffId, true);
    setStaff((prev) =>
      prev.map((entry) =>
        entry.id === staffId
          ? { ...entry, show_in_online_booking: nextValue }
          : entry
      )
    );
    try {
      const res = await fetch(`${API}/staffs/${staffId}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ show_in_online_booking: nextValue }),
      });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(toFriendlyStaffError(detail, res.status));
      }
      const updated = await res.json();
      setStaff((prev) =>
        prev.map((entry) =>
          entry.id === staffId
            ? {
                ...entry,
                show_in_online_booking: !!updated.show_in_online_booking,
              }
            : entry
        )
      );
      showToast("Online booking visibility updated.");
    } catch (err) {
      setStaff((prev) =>
        prev.map((entry) =>
          entry.id === staffId
            ? { ...entry, show_in_online_booking: current }
            : entry
        )
      );
      showToast("Unable to update online booking visibility.");
    } finally {
      setRowSaving(staffId, false);
    }
  };

  const modalText = "text-base md:text-lg";

  const labelText =

    "text-sm md:text-base font-semibold tracking-wide text-slate-500";

  const fieldClass =

    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300";

  const btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full";

  const showReorderNotice = Boolean(reorderNotice);

  const reorderNoticeClass = reorderNotice.toLowerCase().includes("saving")
    ? "text-slate-500"
    : "text-emerald-600";

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="hrSectionShell">
      <div className={`hrSectionWide hr-section-compact ${modalText}`}>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Manage Staff
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div
              id="ms-overview"
              className={
                highlightedSectionId === "ms-overview"
                  ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200 rounded-2xl"
                  : ""
              }
            >
              <ReportHelpCard
                title="Help Guide: Manage Staff"
                subtitle="Create staff profiles, update access, and manage visibility from one page."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="ms-import"
              className={getSectionClassName(
                "ms-import",
                "flex flex-wrap items-center justify-between gap-3"
              )}
            >
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setImportModalOpen(true)}
                  className={BTN_PRIMARY}
                >
                  Import Staff
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/manager/manage/staff-imports")}
                  className={BTN_OUTLINE}
                >
                  Import History
                </button>
                <button
                  type="button"
                  onClick={openInviteModal}
                  className={BTN_OUTLINE}
                >
                  Invite Staff By Email
                </button>
              </div>
              {showReorderNotice && (
                <p className={`text-xs uppercase tracking-wide ${reorderNoticeClass}`}>
                  {reorderNotice}
                </p>
              )}
            </div>


            {error && (

              <div className="flex items-start justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">

                <div className="flex-1">{error}</div>

                <button

                  type="button"

                  className="text-xs text-rose-700/80 hover:text-rose-800 underline"

                  onClick={() => setError("")}

                >

                  Dismiss

                </button>

              </div>

            )}



            {loading ? (
              <div className="text-slate-500">Loading...</div>
            ) : (
              <div className="flex flex-col gap-6">
        <div
                  id="ms-add"
                  className={getSectionClassName(
                    "ms-add",
                    "rounded-2xl border border-slate-200 bg-white p-6 space-y-4"
                  )}
                >
                  <div>

                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">

                      Add Staff

                    </h2>

                    <p className="text-sm text-slate-500">

                      Create a new staff profile and credentials.

                    </p>

                  </div>

                  <form onSubmit={handleCreate}>

                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">

                      <div className="space-y-4">

                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Display Name</span>
                            <InfoTooltip text={TOOLTIPS.display_name} label="Display Name" />
                          </label>
                          <input
                            type="text"
                            value={form.display_name}

                            onChange={(e) =>

                              setForm((f) => ({ ...f, display_name: e.target.value }))

                            }

                            required

                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                          />

                        </div>
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Role</span>
                            <InfoTooltip text={TOOLTIPS.role} label="Role" />
                          </label>
                          <select
                            value={form.role}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, role: e.target.value }))

                            }

                            className={`mt-1 w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                          >

                            {ROLE_OPTIONS.map((option) => (

                              <option

                                key={option.value}

                                value={option.value}

                                disabled={option.value === "owner" && !canAssignOwner}

                              >

                                {option.label}

                              </option>

                            ))}

                          </select>

                        </div>
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Phone</span>
                            <InfoTooltip text={TOOLTIPS.phone} label="Phone" />
                          </label>
                          <input
                            type="text"
                            value={
                              isValidNANP(form.phone)
                                ? formatNANP(form.phone)
                                : form.phone
                            }
                            onChange={(e) => {
                              setForm((f) => ({
                                ...f,
                                phone: toNANPDigits10(e.target.value),
                              }));
                              if (phoneError) {
                                setPhoneError("");
                              }
                            }}
                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
                            placeholder="(555)123-4567"
                          />
                          {phoneError && (
                            <p className="mt-1 text-xs text-rose-600">{phoneError}</p>
                          )}
                        </div>
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Email</span>
                            <InfoTooltip text={TOOLTIPS.email} label="Email" />
                          </label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) =>

                              setForm((f) => ({ ...f, email: e.target.value }))

                            }

                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                            placeholder="staff@example.com"

                            required

                          />

                        </div>

                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Username (optional)</span>
                            <InfoTooltip text={TOOLTIPS.username} label="Username" />
                          </label>
                          <input
                            type="text"
                            value={form.username}

                            onChange={(e) =>

                              setForm((f) => ({ ...f, username: e.target.value }))

                            }

                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                            placeholder="staff-username"

                          />

                          <p className="mt-1 text-xs text-slate-500">

                            Username is for login. Email can also be used.

                          </p>

                        </div>
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Temporary Password (optional)</span>
                            <InfoTooltip text={TOOLTIPS.temp_password} label="Temporary Password" />
                          </label>
                          <input
                            type="password"
                            value={form.temp_password}

                            onChange={(e) =>

                              setForm((f) => ({ ...f, temp_password: e.target.value }))

                            }

                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                            placeholder="At least 8 characters"

                          />

                          <p className="mt-1 text-xs text-slate-500">

                            If left blank, the system will generate one.

                          </p>

                        </div>
                        <div>
                          <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                            <span>Schedule Order (optional)</span>
                            <InfoTooltip text={TOOLTIPS.schedule_order} label="Schedule Order" />
                          </label>
                          <input
                            type="number"
                            step="1"

                            value={form.display_order}

                            onChange={(e) =>

                              setForm((f) => ({ ...f, display_order: e.target.value }))

                            }

                            disabled={!canEditScheduleOrder}

                            className={`mt-1 w-full ${fieldClass} shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:opacity-60 disabled:cursor-not-allowed`}

                            placeholder="e.g. 10"

                          />

                        </div>

                      </div>

                    </div>

                    <div className="mt-6">

                      <button

                        type="submit"

                        disabled={saving}

                        className={`${btnBase} w-full font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed`}

                      >

                        {saving ? "Saving..." : "Add Staff"}

                      </button>

                    </div>

                  </form>

                </div>



                <div
                  id="ms-list"
                  className={getSectionClassName(
                    "ms-list",
                    "rounded-2xl border border-slate-200 bg-white p-6 space-y-4"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Staff
                      </h2>
                      <p className="text-sm text-slate-500">
                        {staffCounts.active} active | {staffCounts.inactive} inactive |{" "}
                        {staffCounts.total} total
                      </p>
                      <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={hideInactiveStaff}
                          onChange={(event) => setHideInactiveStaff(event.target.checked)}
                          className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                        />
                        <span>Hide inactive staff</span>
                      </label>
                    </div>
                  </div>
                  {canEditScheduleOrder && (

                    <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">

                      Drag employees to change schedule order.

                    </p>

                  )}

                  <div
                    id="ms-actions"
                    className={getSectionClassName(
                      "ms-actions",
                      "mt-3 rounded-2xl border border-slate-200 bg-white"
                    )}
                  >
                    <div className="w-full overflow-x-auto overscroll-x-contain">
                    <table className="min-w-[980px] w-full text-sm">

                      <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wide">
                        <tr>
                          <th className="px-4 py-3 text-left whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                              Drag
                              <InfoTooltip text={TOOLTIPS.drag} label="Drag" />
                            </span>
                          </th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Order</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Edit</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Name</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Role</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Phone</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">Active</th>
                          <th className="px-4 py-3 text-left whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                              Actions
                              <InfoTooltip
                                text={`${TOOLTIPS.perform_services} ${TOOLTIPS.uses_time_clock} ${TOOLTIPS.online_booking}`}
                                label="Actions"
                              />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <DndContext

                        sensors={sensors}

                        collisionDetection={closestCenter}

                        onDragEnd={handleDragEnd}

                      >

                        <SortableContext
                          items={visibleStaff.map((entry) => entry.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody className="divide-y divide-slate-200">
                            {visibleStaff.map((s) => (
                              <SortableStaffRow
                                key={s.id}
                                staffMember={s}
                                canDrag={canEditScheduleOrder && !reorderSaving}
                                onToggleActive={toggleActive}
                                onTogglePerformServices={togglePerformServices}
                                onToggleUsesTimeClock={toggleUsesTimeClock}
                                onToggleShowOnlineBooking={toggleShowOnlineBooking}
                                orderValue={
                                  s.display_order ?? fallbackOrderById.get(s.id) ?? "-"
                                }
                                isSaving={savingById[s.id]}
                                canManageTimeClock={canManageTimeClock}
                                canManageOnlineBooking={canManageOnlineBooking}
                              />
                            ))}
                            {!visibleStaff.length && (
                              <tr>
                                <td
                                  className="px-4 py-4 text-slate-500 text-center"
                                  colSpan={8}
                                >
                                  {hideInactiveStaff
                                    ? "No active staff to display."
                                    : "No staff yet."}
                                </td>
                              </tr>
                            )}
                          </tbody>

                        </SortableContext>

                      </DndContext>

                    </table>
                    </div>

                  </div>

                </div>

              </div>

            )}

          </div>

        </div>

      </div>

      <StaffCredentialModal
        credential={credentialModal}
        onClose={() => setCredentialModal(null)}
      />
      <SBINModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title="Import Staff"
        size="xxl"
      >
        <StaffImportWizard
          onComplete={async () => {
            await refreshStaff();
            setImportModalOpen(false);
          }}
          onClose={() => setImportModalOpen(false)}
          initialSalonId={salonId}
        />
      </SBINModal>
      <SBINModal
        isOpen={inviteModalOpen}
        onClose={closeInviteModal}
        title="Invite Staff By Email"
        size="xl"
        fitContent
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hasMultipleInviteSalons ? (
              <div>
                <label className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                  <span>Salon Location</span>
                  <InfoTooltip text={TOOLTIPS.invite_salon} label="Salon Location" />
                </label>
                <select
                  value={inviteSelectedSalonId || ""}
                  onChange={(event) => {
                    const nextId = Number(event.target.value);
                    setInviteSelectedSalonId(
                      Number.isFinite(nextId) && nextId > 0 ? nextId : null
                    );
                  }}
                  className={`mt-1 w-full ${FIELD_CLASS}`}
                >
                  <option value="">Select salon</option>
                  {inviteSalonOptions.map((entry) => (
                    <option key={`invite-salon-${entry.id}`} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                  <span>Salon Location</span>
                  <InfoTooltip text={TOOLTIPS.invite_salon} label="Salon Location" />
                </label>
                <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:text-base text-slate-700">
                  {salonName || "Current salon"}
                </div>
              </div>
            )}
            <div>
              <label className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                <span>Invitation Expiration</span>
                <InfoTooltip
                  text={TOOLTIPS.invite_expiration}
                  label="Invitation Expiration"
                />
              </label>
              <select
                value={inviteExpiration}
                onChange={(event) => setInviteExpiration(event.target.value)}
                className={`mt-1 w-full ${FIELD_CLASS}`}
              >
                {INVITE_EXPIRATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
              <span>Invite Existing Staff</span>
              <InfoTooltip text={TOOLTIPS.invite_staff} label="Invite Existing Staff" />
            </div>
            <div className="mt-2 rounded-2xl border border-slate-200 bg-white p-3 max-h-52 overflow-y-auto">
              {staffWithEmail.length ? (
                <div className="space-y-2">
                  {staffWithEmail.map((entry) => (
                    <label
                      key={`invite-staff-${entry.id}`}
                      className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm md:text-base text-slate-700 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={inviteStaffIds.includes(entry.id)}
                        onChange={() => toggleInviteStaffId(entry.id)}
                        className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                      />
                      <span className="font-semibold text-slate-800">
                        {entry.display_name || entry.name || "Staff"}
                      </span>
                      <span className="text-slate-500">{entry.email}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  No existing staff with email addresses found.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
              <span>Recipient Emails</span>
              <InfoTooltip text={TOOLTIPS.invite_recipients} label="Recipient Emails" />
            </label>
            <textarea
              rows={5}
              value={inviteRecipientText}
              onChange={(event) => setInviteRecipientText(event.target.value)}
              placeholder="newstaff1@example.com, newstaff2@example.com"
              className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>

          {inviteFormError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {inviteFormError}
            </div>
          ) : null}

          {inviteFailures.length ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                Some invites failed
              </p>
              <div className="mt-2 max-h-28 overflow-y-auto space-y-1 text-sm text-amber-800">
                {inviteFailures.map((item, idx) => (
                  <p key={`invite-failure-${idx}`}>
                    {(item?.email || "Unknown email")}: {toInviteFailureMessage(item)}
                  </p>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className={BTN_OUTLINE}
              disabled={inviteSending}
              onClick={closeInviteModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={inviteSending}
              onClick={handleSendInvites}
            >
              {inviteSending ? "Sending..." : "Send Invitation"}
            </button>
          </div>
        </div>
      </SBINModal>
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
